import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { getAchievements } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';

const BORMULA_LOGO = "https://customer-assets.emergentagent.com/job_a6e857fc-9f8a-42a4-a234-830c8a3cc714/artifacts/98ozj9ol_Bormula%20Live%20Logo%201080x1350%20%281%29.png";

export default function StoryPage() {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: 'Our Story',
    description: 'The journey of Bormula — milestones from the fastest-growing F1 media brand built for the internet-native fan.',
    url: 'https://bormula.com/story',
  });

  useEffect(() => {
    getAchievements(true)
      .then(setAchievements)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  const byYear = achievements.reduce((acc, a) => {
    const year = (a.date || '').slice(0, 4) || 'Undated';
    (acc[year] = acc[year] || []).push(a);
    return acc;
  }, {});
  const years = Object.keys(byYear).sort((a, b) => b.localeCompare(a));

  return (
    <div className="min-h-screen bg-black text-white" data-testid="story-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="story-logo-link">
            <img src={BORMULA_LOGO} alt="Bormula" className="h-10 w-10 object-contain" />
            <span className="font-display font-bold text-2xl tracking-tighter">BORMULA</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-[#CCFF00] transition-colors" data-testid="story-back-link">
            <ArrowLeft size={16} /> Back Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="pt-36 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="font-mono text-xs text-[#CCFF00] uppercase tracking-[0.3em] mb-4">The Journey</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
            Our <span className="text-[#CCFF00]">Story</span>
          </h1>
          <p className="font-body text-white/50 text-base mt-6 max-w-2xl">
            From a fan account to the fastest source for F1 culture. Every milestone that got us here.
          </p>
        </div>
      </header>

      {/* Timeline */}
      <main className="px-6 pb-32">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner" /></div>
          ) : years.length === 0 ? (
            <p className="text-white/40 font-body py-20 text-center">The story is being written. Check back soon.</p>
          ) : (
            years.map((year) => (
              <div key={year} className="relative mb-8" data-testid={`story-year-${year}`}>
                <span className="font-display font-black text-8xl sm:text-9xl text-white/5 select-none block leading-none">{year}</span>

                <div className="border-l-2 border-zinc-800 ml-4 -mt-6">
                  {byYear[year].map((achievement) => (
                    <article key={achievement.id} className="relative pl-8 pb-14 group" data-testid={`story-milestone-${achievement.id}`}>
                      <div className="absolute -left-[5px] top-2 w-2 h-2 bg-zinc-700 group-hover:bg-[#CCFF00] transition-colors" />
                      <time className="font-mono text-xs uppercase tracking-wider text-white/40">{formatDate(achievement.date)}</time>
                      <h3 className="font-display font-bold text-2xl sm:text-3xl uppercase tracking-tight mt-2 group-hover:text-[#CCFF00] transition-colors">
                        {achievement.title}
                      </h3>
                      {achievement.description && (
                        <p className="font-body text-white/50 mt-3 max-w-xl leading-relaxed">{achievement.description}</p>
                      )}
                      {achievement.link && (
                        <a
                          href={achievement.link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 mt-3 text-xs font-bold uppercase tracking-widest text-[#CCFF00]/70 hover:text-[#CCFF00] transition-colors"
                        >
                          See it <ExternalLink size={12} />
                        </a>
                      )}
                    </article>
                  ))}
                </div>
              </div>
            ))
          )}

          {!loading && years.length > 0 && (
            <div className="flex items-center gap-4 ml-4 pl-8 border-l-2 border-transparent">
              <div className="w-2 h-2 bg-[#CCFF00] -ml-[41px]" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#CCFF00]">The story continues...</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
